import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { PackPickService } from '../../api/services/packPickService';
import { IOrderPackage, IPackedOrderHistoryEntry } from '../../types/api/response/packpickresponse';
import { PackageBarcodeCard } from '../../components/PackageBarcodeCard';

interface Props {
  orderNumber: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
}

const formatDate = (val?: string | null) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

export const PackedOrderDetailDialog = ({ orderNumber, isOpenDialog, setIsOpenDialog }: Props) => {
  const dispatch = useDispatch();
  const [history, setHistory] = useState<IPackedOrderHistoryEntry[]>([]);
  const [packages, setPackages] = useState<IOrderPackage[]>([]);
  const [packagesFailed, setPackagesFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpenDialog) return;
    setHistory([]);
    setPackages([]);
    setPackagesFailed(false);
    setLoading(true);
    // Loaded independently so a packages failure doesn't hide the history (and vice versa).
    const historyRequest = PackPickService.getPackedOrderHistory(orderNumber)
      .then(setHistory)
      .catch((e) => {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to load packed order details.',
            severity: 'error',
          }),
        );
      });
    const packagesRequest = PackPickService.getPackedOrderPackages(orderNumber)
      .then(setPackages)
      .catch(() => setPackagesFailed(true));
    Promise.all([historyRequest, packagesRequest]).finally(() => setLoading(false));
  }, [isOpenDialog, orderNumber, dispatch]);

  const handleClose = () => setIsOpenDialog(false);

  return (
    <Dialog
      open={isOpenDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { maxHeight: '90vh' } },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography fontWeight={600}>Packed Order — {orderNumber}</Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'grey.700' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading ? (
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 2 }}>
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Packages
            </Typography>
            {packagesFailed ? (
              <Typography variant="body2" color="error">
                Failed to load packages.
              </Typography>
            ) : packages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No packages recorded for this order.
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {packages.map((pkg) => (
                  <PackageBarcodeCard
                    key={pkg.barcode}
                    code={pkg.barcode}
                    packType={pkg.pack_type}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            History
          </Typography>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No packing history recorded for this order.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Station</TableCell>
                  <TableCell>Pack Start</TableCell>
                  <TableCell>Pack End</TableCell>
                  <TableCell>Recorded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Chip label={entry.status} size="small" />
                    </TableCell>
                    <TableCell>{entry.station_name ?? '—'}</TableCell>
                    <TableCell>{formatDate(entry.pack_start)}</TableCell>
                    <TableCell>{formatDate(entry.pack_end)}</TableCell>
                    <TableCell>{formatDate(entry.received_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
